import React from 'react';
import Hero from './Hero';
import { 
  Container, 
  ContentSection, 
  SectionTitle, 
  SectionContent,
  DeveloperCredit,
  DisclaimerText
} from './styled';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PrimaryButton } from '../Shared/Button';

const Landing = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const handleExploreTool = () => {
    isLoggedIn ? navigate('/dashboard') : navigate('/signup');
  };

  return (
    <Container className="landing-container">
      <Hero />
      
      <ContentSection>
        <SectionTitle>About the tool</SectionTitle>
        <SectionContent>
          <p>
            <strong>Advancing fusion commercialization through transparent, evidence-based data</strong>
          </p>
          <p>
            Commercial fusion energy is moving closer to reality—but credible, transparent cost insights are essential to guide research priorities, policy decisions, and investment strategies.
          </p>
          <p>
            To meet this need, Clean Air Task Force developed the Fusion Cost Model, informed by more than a decade of collaboration with fusion developers, industry leaders, and international institutions. The model gives users a clear and as complete as possible view of the cost categories present in a fusion power plant and helps to identify key cost drivers shaping different fusion power plant designs and enables consistent, apples-to-apples economic analysis across approaches.
          </p>
          <p>
            The tool includes two technology-specific costing frameworks covering magnetic confinement (MFE) and inertial confinement (IFE).
          </p>
          <p>
            This model builds on Woodruff Scientific Ltd.'s extensive experience in fusion energy cost analysis developed over the past several years. It reflects close collaboration with leaders across the fusion community and draws on a series of foundational papers published since 2011 that helped establish early consensus on the "Path to Market" for fusion energy systems.
          </p>
          <p>
            The model incorporates the latest thinking in the field, bringing together current best practices in cost estimation identified through ARPA-E–supported work, and extends them by integrating new elements such as safety-related costs. By integrating decades of research, experimental results, and economic assumptions into a single, accessible framework, the model offers a qualitative tool to better understand the economic viability of emerging fusion technologies. Designed to evolve over time, the tool will continue to incorporate input from the global fusion community, including investors, researchers, national laboratories, and technology developers.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Why CATF</SectionTitle>
        <SectionContent>
          <p>
            Deploying commercial fusion power requires more than technical breakthroughs—it also requires a clear understanding of costs, risks, and economic viability across multiple technology pathways.
          </p>
          <p>
            As an independent nonprofit with a strong record of advancing clean energy solutions, CATF is uniquely positioned to lead this effort.
          </p>
        </SectionContent>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>Why it matters</SectionTitle>
        <SectionContent>
          <p>
            Investors, policymakers, and developers need robust cost data to guide fusion commercialization. Yet, until now, fusion cost estimates and full cost categories have been fragmented, inconsistent, or based on assumptions that are difficult to reproduce.
          </p>
          <p><strong>The Fusion Cost Model aims to address this gap by:</strong></p>
          <ul>
            <li>Providing a transparent, reproducible framework for evaluating costs across multiple fusion technologies</li>
            <li>Allowing scenario modeling to test different design choices and deployment pathways and provide the resulting implications on cost</li>
            <li>Supporting investors in identifying high-impact opportunities and assessing financial feasibility</li>
            <li>Informing policymakers and research funders on where resources can most effectively accelerate commercialization</li>
          </ul>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>How it works</SectionTitle>
        <SectionContent>
          <p>
            The model integrates experimental data, engineering assumptions, and economic parameters into a configurable framework. Users can adjust inputs such as machine size, technology type, capital costs, and operational parameters to explore different scenarios and understand their cost implications.
          </p>
          <p>
            Outputs include projected capital and operational costs, levelized cost of electricity, and sensitivity analyses that highlight key drivers of financial and technical risk.
          </p>
          <p>
            All assumptions, methodologies, and sources are fully documented based on the information available today, allowing users to reproduce results, compare scenarios, and adapt the model for their own research or investment analysis.
          </p>
          <p>
            The model is powered by the open-source{' '}
            <a
              href="https://github.com/Woodruff-Scientific-Ltd/PyFECONS"
              target="_blank"
              rel="noopener noreferrer"
            >
              PyFECONs (Python Fusion Economics)
            </a>{' '}
            library.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Limitations of the model</SectionTitle>
        <SectionContent>
          <p>
            The objective of this study is to present a methodological framework for understanding the cost structure of a fusion power plant. To this end, the model integrates a comprehensive—though not exhaustive or fully accurate—set of cost categories, including pre-construction costs, direct costs, indirect service costs, owner's costs, supplementary costs, and financial costs.
          </p>
          <p>
            The outputs generated by the model are not intended to represent absolute or realistic values of the Levelized Cost of Electricity (LCOE) for the simulated plant. Instead, the results should be interpreted solely as relative indicators, suitable for comparison with other similar modelling exercises and for identifying the primary cost drivers within the plant configuration analyzed.
          </p>
          <p>
            The cost elements currently included in the model are incomplete and do not fully capture the cost structure of a future commercial fusion power plant. Consequently, the absolute LCOE values derived from this model should not be considered accurate or representative of real-world deployment.
          </p>
          <p>
            The model will be progressively refined as additional data become available and as technological and economic parameters are better understood, with the long-term objective of producing LCOE estimates that more closely reflect the realities of commercial fusion power plants.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Get Involved</SectionTitle>
        <SectionContent>
          <p>
            The Fusion Cost Model is a living, community-driven resource. We invite researchers, investors, technology developers, and policymakers to explore the model, share feedback, and propose improvements.
          </p>
          <p>
            Our goal is to continuously refine the model so that its levelized cost of electricity (LCOE) estimates accurately and quantitatively reflect real-world conditions. While the model is still evolving, the foundations are in place—and community input is essential to making it stronger.
          </p>
          <p>
            Contact us at <a href="mailto:info@catf.us">info@catf.us</a> to get involved or learn more.
          </p>
          <PrimaryButton onClick={handleExploreTool}>
            Get Started
            <i className="fas fa-arrow-right"></i>
          </PrimaryButton>
        </SectionContent>
      </ContentSection>

      <DeveloperCredit>
        <p>Developed by <strong>Clean Air Task Force</strong></p>
        <DisclaimerText>
          <p><strong>Disclaimer</strong></p>
          <p>
            The objective of this study is to present a methodological framework for understanding the cost structure of a fusion power plant. To this end, the model integrates a comprehensive—though not exhaustive or fully accurate—set of cost categories, including pre-construction costs, direct costs, indirect service costs, owner's costs, supplementary costs, and financial costs.
          </p>
          <p>
            The outputs generated by the model are not intended to represent absolute or realistic values of the Levelized Cost of Electricity (LCOE) for the simulated plant. Instead, the results should be interpreted solely as relative indicators, suitable for comparison with other similar modelling exercises and for identifying the primary cost drivers within the plant configuration analysed.
          </p>
          <p>
            The cost elements currently included in the model are incomplete and do not fully capture the cost structure of a future commercial fusion power plant. Consequently, the absolute LCOE values derived from this model should not be considered accurate or representative of real-world deployment.
          </p>
          <p>
            The model will be progressively refined as additional data become available and as technological and economic parameters are better understood, with the long-term objective of producing LCOE estimates that more closely reflect the realities of commercial fusion power plants.
          </p>
        </DisclaimerText>
      </DeveloperCredit>
    </Container>
  );
};

export default Landing;
